import {Graphics, Rectangle} from "pixi.js";
import {inches, lineBlueHeavy, lineBlueLight} from "../const";
import {GlyphRect} from "./hardwareGlyphs/HardwareGlyph";

// both the lever and the deadbolt are bored through a standard 2 1/8" hole
// centered on (x, y); their circles share this radius (scene units = mm).
const BORE_RADIUS = (2 + 1/8) / 2 * inches;

// door lever: a thin bar reaching in from the bored hub.
const LEVER_LENGTH = 4 * inches;
const LEVER_THICKNESS = 0.75 * inches;

// outline weight for a bored hole and for the deadbolt's ring + keyway slot.
const BORE_STROKE = 0.3 * inches;

export type DoorHardwareKind = "handle" | "lock";

// Draws a piece of door hardware at (x, y) in `g`'s local coords: a lever for
// "handle", a deadbolt for "lock". `selected` brightens it. Shared by the
// frame-grid DoorFace and the door-elevation DoorLeaf so both views render
// hardware identically. (x, y) is the hub point — the latch-side edge offset by
// the item's backset, at its `top` height. `dir` is the horizontal direction
// toward the door centre (+1 = right, -1 = left, i.e. the caller's insetSign):
// the lever reaches inward from the hub that way. The lock is symmetric and
// ignores `dir`.
export function renderHardwareItem(g: Graphics, x: number, y: number, kind: DoorHardwareKind, selected: boolean, dir: number) {
    const color = selected ? lineBlueLight : lineBlueHeavy;
    const alpha = selected ? 1 : 0.5;

    if (kind === "handle") {
        g.lineStyle(0);
        g.beginFill(color, alpha);
        // lever reaches from the bored hub toward the door centre.
        const tweak = 1;
        g.drawRect(dir < 0 ? x - LEVER_LENGTH - BORE_RADIUS + tweak : x + BORE_RADIUS-tweak, y - LEVER_THICKNESS / 2, LEVER_LENGTH, LEVER_THICKNESS);
        g.drawCircle(x, y, BORE_RADIUS);
        g.endFill();
        return;
    }

    // lock: outline ring on the bore + a vertical keyway slot through the middle.
    g.lineStyle(BORE_STROKE, color, alpha);
    g.beginFill(0, 0);
    g.drawCircle(x, y, BORE_RADIUS);
    g.endFill();

    g.lineStyle(0);
    g.beginFill(color, alpha);
    g.drawRect(x - BORE_STROKE, y - BORE_RADIUS * 0.6, BORE_STROKE * 2, BORE_RADIUS * 1.2);
    g.endFill();
}

// the area a lever / deadbolt covers, in the same local coords as renderHardwareItem's
// (x, y): the bore, plus the lever reaching `dir`-ward from it.
export function hardwareFootprint(x: number, y: number, kind: DoorHardwareKind, dir: number): GlyphRect {
    const bore: GlyphRect = {x0: x - BORE_RADIUS, y0: y - BORE_RADIUS, x1: x + BORE_RADIUS, y1: y + BORE_RADIUS};
    if (kind !== "handle") return bore;

    const inner = x + dir * (BORE_RADIUS + LEVER_LENGTH);
    return {...bore, x0: Math.min(bore.x0, inner), x1: Math.max(bore.x1, inner)};
}

// Draws the bored hole on its own at (x, y) in `g`'s local coords: an outline
// circle, no hardware glyph. Used by the door elevation, which shows the preps
// cut into the leaf rather than the hardware mounted on it. `selected` brightens it.
export function renderHardwareBore(g: Graphics, x: number, y: number, selected: boolean) {
    const color = selected ? lineBlueLight : lineBlueHeavy;
    const alpha = selected ? 1 : 0.5;

    g.lineStyle(BORE_STROKE, color, alpha);
    g.beginFill(0, 0);
    g.drawCircle(x, y, BORE_RADIUS);
    g.endFill();
}

// a padded square covering the bore, in the same local coords as
// renderHardwareBore's (x, y). Used to make the (thin) outline easy to click.
export function boreHitArea(x: number, y: number): Rectangle {
    const pad = BORE_RADIUS * 0.75;
    const size = 2 * (BORE_RADIUS + pad);
    return new Rectangle(x - BORE_RADIUS - pad, y - BORE_RADIUS - pad, size, size);
}

// a padded rectangle covering the hub + lever, in the same local coords as
// renderHardwareItem's (x, y). Used to make the (thin) hardware easy to click.
export function hardwareHitArea(x: number, y: number, dir: number): Rectangle {
    const inner = x + dir * LEVER_LENGTH;
    const left = Math.min(x - BORE_RADIUS, inner);
    const right = Math.max(x + BORE_RADIUS, inner);
    const pad = BORE_RADIUS * 0.75;
    return new Rectangle(left - pad, y - BORE_RADIUS - pad, (right - left) + 2 * pad, 2 * (BORE_RADIUS + pad));
}
